import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { CrudBaseService } from '../crud.service';
import { TradingGroup } from '../models/trading-group';
import { FilterObjectBuilder, FilterOperator } from '../param-builders';
import { TradingAccountType } from '../models';
import { isNil, omit } from 'lodash';
import { PlatformLookup } from '@shared/models/platform-lookup';

export interface GenerateTradingGroup {
    platform?: string;
    tenant?: string;
    trading_account_type?: string;
    currency?: string;
}

@Injectable()
export class TradingGroupService extends CrudBaseService<TradingGroup> {
    constructor(httpClient: HttpClient) {
        super('/tradinggroup', httpClient);
    }

    getTradingGroup(
        platform: PlatformLookup,
        tenant: string,
        selectedCurrency: string,
        groupName?: string
    ): Observable<TradingGroup[]> {
        const filterParams = new FilterObjectBuilder();
        const selectedPlatform = platform === PlatformLookup.TV ? PlatformLookup.MT5L : platform;
        filterParams.byObject({ platform: selectedPlatform, tenant: tenant }, FilterOperator.eq);
        filterParams.by('trading_group', FilterOperator.like, selectedCurrency);

        if (!!groupName) {
            filterParams.by('trading_group', FilterOperator.like, groupName);
        }

        return this.filter(filterParams.apiFilter());
    }

    generateTradingGroupForApplicationForm(body: GenerateTradingGroup): Observable<string> {
        const shortenPlatform = body.platform.split('-')[0];

        return this.httpClient.get('assets/lookup/trading_groups.json').pipe(
            map((value: any) => {
                return value[shortenPlatform].find(
                    (tg: string) => tg.indexOf(body.currency) !== -1 && tg.indexOf(body.trading_account_type) !== -1
                );
            })
        );
    }

    generateTradingGroupForDemo(body: GenerateTradingGroup): Observable<string> {
        const shortenPlatform = body.platform.split('-')[0];

        return this.httpClient.get('assets/lookup/trading_groups_demo.json').pipe(
            map((value: any) => {
                return value[shortenPlatform].find((tradingGroup: string) => {
                    const tradingGroupUppercase = tradingGroup.toUpperCase();
                    return (
                        tradingGroupUppercase.indexOf(body.currency) !== -1 &&
                        tradingGroupUppercase.indexOf(body.trading_account_type) !== -1
                    );
                });
            })
        );
    }

    getByTradingGroup(id: string, tradingGroup: string) {
        return this.httpClient
            .get<TradingGroup>(`${this.basePath}/${id}?name=${encodeURIComponent(tradingGroup)}`)
            .pipe(map((res: any) => res && res.data));
    }

    patchByTradingGroup(id: string, tradingGroup: string, model: TradingGroup): Observable<any> {
        return this.httpClient.patch(`${this.basePath}/${id}?name=${encodeURIComponent(tradingGroup)}`, {
            data: omit(model, ['id'])
        });
    }

    approveTradingGroup(id: string, tradingGroup: string) {
        return this.httpClient
            .get<TradingGroup>(`${this.basePath}/${id}/approve?name=${encodeURIComponent(tradingGroup)}`)
            .pipe(map((res: any) => res && res.data));
    }
}
